import { type CustomTooltipProps } from "ag-grid-react"

import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"

import HeaderTooltip from "./HeaderTooltip"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useAdjustments } from "~/hooks/useAdjustments"
import { api } from "~/utils/api"
import { useForecastingChannels } from "~/hooks/useChannels"
import { aggregateGlobal } from "~/utils/adjustmentsUtility"
import { capitalize } from "lodash"
import { AdjustmentLock } from "../../AdjustmentLock"
import { AdjustmentRow } from "../../AdjustmentRow"
import { useMemo } from "react"
import LoadingOverlay from "../../LoadingOverlay"

export default function AdjustmentTooltip(props: CustomTooltipProps) {
  const variants = props.location === "cell" ? (props.value as IVariantSummary[]) : []
  const variantIds = variants?.map((variant) => variant.variantId) ?? []
  const { data: adjustments, isLoading: isLoadingAdjustments } = useAdjustments(
    variantIds,
    undefined,
  )
  const { data: warehouses, isLoading: isLoadingWarehouses } = api.warehouse.get.useQuery()
  const { data: channels, isLoading: isLoadingChannels } = useForecastingChannels()

  const aggregatedAdjustments = useMemo(() => {
    if (!adjustments || !warehouses) return []
    return aggregateGlobal(adjustments, warehouses)
  }, [adjustments, warehouses])

  const getChannelNames = (channelIds: string[]) => {
    if (!channels) return []
    return channelIds
      .map((id) => channels.find((c) => c.id === id)?.name)
      .filter(Boolean) as string[]
  }

  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }

  if (isLoadingAdjustments || isLoadingWarehouses || isLoadingChannels) {
    return (
      <Card>
        <LoadingOverlay />
      </Card>
    )
  }

  return (
    <Card className="w-[300px]">
      <CardHeader>
        <CardTitle>Adjustments</CardTitle>
      </CardHeader>
      <CardContent className="p-0 m-0 max-h-[300px] overflow-y-auto">
        <div className="divide-y divide-base">
          {aggregatedAdjustments.map((adj) => (
            <AdjustmentRow
              key={adj.name}
              name={adj.name}
              value={adj.value}
              type={adj.type}
              startDate={adj.startDate}
              endDate={adj.endDate}
              leading={<AdjustmentLock locksForecast={adj.locksForecast} size={3} />}
            >
              <div className="text-fg-subtle paragraph-sm mt-0.5">
                {capitalize(adj.theme)} for {adj.variantIds.length} variants
              </div>
              <div className="flex items-center">
                <AdjustmentLock locksForecast={adj.locksForecast} size={2} />
                <span className="text-fg-subtle paragraph-sm">
                  Warehouse:{" "}
                  {warehouses?.find((w) => w.id === adj.warehouseId)?.name ?? adj.warehouseId}
                </span>
                {adj.channels && adj.channels.length > 0 && (
                  <span className="text-fg-subtle paragraph-sm ml-2">
                    Channel:{" "}
                    {adj.channels.length > 1 ? "All" : getChannelNames(adj.channels).join(", ")}
                  </span>
                )}
              </div>
            </AdjustmentRow>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
